// Onsite lodging date logic for the Atlantic Center for the Arts
// (the "Artist's Retreat" / "Retreat Center").
//
// Year-specific festival windows live in iabf-festival-dates.js.
// An "extra night" is a night a guest sleeps over, so:
//   - the earliest requested BEFORE-night becomes the check-in date
//   - check-out is the morning AFTER the latest requested AFTER-night
//
// This is the single source of truth shared by the dancer Lodging page,
// the admin dancer view, and the rooming-list export, so they never drift.

import { festivalWeek } from '@/config/iabf-festival-dates'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'

const week = () => festivalWeek(IABF_DANCER_YEAR)

export function getDefaultCheckIn (year = IABF_DANCER_YEAR) {
  return festivalWeek(year).defaultCheckIn
}

export function getDefaultCheckOut (year = IABF_DANCER_YEAR) {
  return festivalWeek(year).defaultCheckOut
}

// Back-compat exports (current dancer year)
export const DEFAULT_CHECK_IN = getDefaultCheckIn()
export const DEFAULT_CHECK_OUT = getDefaultCheckOut()

/**
 * Extra-night windows relative to the current dancer-year festival.
 * 2027 base: Mon Jul 12 – Sat Jul 17.
 * 2026 base: Mon Jul 6 – Sat Jul 11.
 */
function extraNightTables (year = IABF_DANCER_YEAR) {
  const y = Number(year)
  if (y === 2026) {
    return {
      before: [
        { field: 'extraNight1', night: 'Friday, July 3, 2026', checkIn: 'Friday, July 3, 2026 at 4 PM' },
        { field: 'extraNight2', night: 'Saturday, July 4, 2026', checkIn: 'Saturday, July 4, 2026 at 4 PM' },
        { field: 'extraNight3', night: 'Sunday, July 5, 2026', checkIn: 'Sunday, July 5, 2026 at 4 PM' }
      ],
      after: [
        { field: 'extraNight4', night: 'Saturday, July 11, 2026', checkOut: 'Sunday, July 12, 2026 at 11 AM' },
        { field: 'extraNight5', night: 'Sunday, July 12, 2026', checkOut: 'Monday, July 13, 2026 at 11 AM' },
        { field: 'extraNight6', night: 'Monday, July 13, 2026', checkOut: 'Tuesday, July 14, 2026 at 11 AM' }
      ]
    }
  }
  // 2027 (and default)
  return {
    before: [
      { field: 'extraNight1', night: 'Friday, July 9, 2027', checkIn: 'Friday, July 9, 2027 at 4 PM' },
      { field: 'extraNight2', night: 'Saturday, July 10, 2027', checkIn: 'Saturday, July 10, 2027 at 4 PM' },
      { field: 'extraNight3', night: 'Sunday, July 11, 2027', checkIn: 'Sunday, July 11, 2027 at 4 PM' }
    ],
    after: [
      { field: 'extraNight4', night: 'Saturday, July 17, 2027', checkOut: 'Sunday, July 18, 2027 at 11 AM' },
      { field: 'extraNight5', night: 'Sunday, July 18, 2027', checkOut: 'Monday, July 19, 2027 at 11 AM' },
      { field: 'extraNight6', night: 'Monday, July 19, 2027', checkOut: 'Tuesday, July 20, 2027 at 11 AM' }
    ]
  }
}

const isPresent = value => !!value
const statusOf = value => (typeof value === 'string' && value ? value : 'requested')

export function computeCheckIn (travelDetails, year = IABF_DANCER_YEAR) {
  const defaultIn = getDefaultCheckIn(year)
  if (!travelDetails) {
    return defaultIn
  }
  const { before } = extraNightTables(year)
  for (const night of before) {
    if (isPresent(travelDetails[night.field])) {
      return night.checkIn
    }
  }
  return defaultIn
}

export function computeCheckOut (travelDetails, year = IABF_DANCER_YEAR) {
  const defaultOut = getDefaultCheckOut(year)
  if (!travelDetails) {
    return defaultOut
  }
  let checkOut = defaultOut
  const { after } = extraNightTables(year)
  for (const night of after) {
    if (isPresent(travelDetails[night.field])) {
      checkOut = night.checkOut
    }
  }
  return checkOut
}

// Human list of requested extra nights with status
export function extraNightsSummary (travelDetails, year = IABF_DANCER_YEAR) {
  if (!travelDetails) {
    return ''
  }
  const { before, after } = extraNightTables(year)
  const labels = []
  for (const night of [...before, ...after]) {
    const value = travelDetails[night.field]
    if (isPresent(value)) {
      labels.push(`${night.night} (${statusOf(value)})`)
    }
  }
  return labels.join('; ')
}

export function hasPendingExtraNights (travelDetails, year = IABF_DANCER_YEAR) {
  if (!travelDetails) {
    return false
  }
  const { before, after } = extraNightTables(year)
  return [...before, ...after].some(night => travelDetails[night.field] === 'requested')
}

// Nights count for an onsite stay. Base festival window is 5 nights.
export const DEFAULT_NIGHTS = 5
export function computeNights (travelDetails, year = IABF_DANCER_YEAR) {
  if (!travelDetails) {
    return DEFAULT_NIGHTS
  }
  const { before, after } = extraNightTables(year)
  let extras = 0
  for (const night of [...before, ...after]) {
    if (isPresent(travelDetails[night.field])) {
      extras++
    }
  }
  return DEFAULT_NIGHTS + extras
}

/** ISO cutoffs for the dancer lodging form extra-night checkboxes. */
export function extraNightIsoWindows (year = IABF_DANCER_YEAR) {
  const y = Number(year)
  if (y === 2026) {
    return {
      arrivalDefault: '2026-07-06',
      departureDefault: '2026-07-11',
      before1Max: '2026-07-03',
      before2Dates: ['2026-07-03', '2026-07-04'],
      before3Dates: ['2026-07-03', '2026-07-04', '2026-07-05'],
      after4Dates: ['2026-07-12', '2026-07-13'],
      after4Min: '2026-07-14',
      after5Dates: ['2026-07-13'],
      after5Min: '2026-07-14',
      after6Min: '2026-07-14',
      labels: {
        n1: 'Friday, July 3, 2026',
        n2: 'Saturday, July 4, 2026',
        n3: 'Sunday, July 5, 2026',
        n4: 'Saturday, July 11, 2026',
        n5: 'Sunday, July 12, 2026',
        n6: 'Monday, July 13, 2026'
      }
    }
  }
  return {
    arrivalDefault: '2027-07-12',
    departureDefault: '2027-07-17',
    before1Max: '2027-07-09',
    before2Dates: ['2027-07-09', '2027-07-10'],
    before3Dates: ['2027-07-09', '2027-07-10', '2027-07-11'],
    after4Dates: ['2027-07-18', '2027-07-19'],
    after4Min: '2027-07-20',
    after5Dates: ['2027-07-19'],
    after5Min: '2027-07-20',
    after6Min: '2027-07-20',
    labels: {
      n1: 'Friday, July 9, 2027',
      n2: 'Saturday, July 10, 2027',
      n3: 'Sunday, July 11, 2027',
      n4: 'Saturday, July 17, 2027',
      n5: 'Sunday, July 18, 2027',
      n6: 'Monday, July 19, 2027'
    }
  }
}

// silence unused if tree-shaken wrong
void week
